import { useCallback, useEffect, useSyncExternalStore, useState } from 'react';
import { Bug, ChevronDown, ChevronUp, Trash2, X } from 'lucide-react';
import {
  isEngineDebugEnabled,
  getDebugEntries,
  clearDebugEntries,
  subscribeDebug,
} from '@/lib/engine-debug';
import type { EngineDebugEntry } from '@/lib/engine-debug';

/* ── Helpers ──────────────────────────────────────────── */

function timeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return new Date(ts).toLocaleTimeString();
}

function currency(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/* ── Row ─────────────────────────────────────────────── */

function DebugRow({ entry }: { entry: EngineDebugEntry }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-border-subtle last:border-b-0">
      <button
        className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-foreground-soft/50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span
          className={`h-2 w-2 rounded-full shrink-0 ${
            entry.output.matched ? 'bg-green-400' : 'bg-amber-400'
          }`}
        />
        <span className="text-xs font-mono text-primary-foreground truncate flex-1">
          {entry.slug}
        </span>
        <span className="text-xs text-muted-foreground shrink-0">
          {timeAgo(entry.timestamp)}
        </span>
        {open ? (
          <ChevronUp className="h-3 w-3 text-muted-foreground shrink-0" />
        ) : (
          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
        )}
      </button>

      {open && (
        <div className="px-2 pb-2 space-y-1">
          {/* Input */}
          <div>
            <span className="text-xs text-muted-foreground uppercase tracking-wide">
              Input
            </span>
            <pre className="text-xs text-muted-foreground bg-foreground rounded px-1.5 py-1 mt-0.5 overflow-x-auto max-h-24 font-mono">
              {JSON.stringify(entry.input, null, 2)}
            </pre>
          </div>
          {/* Output */}
          <div>
            <span className="text-xs text-muted-foreground uppercase tracking-wide">
              Output
            </span>
            <div className="text-xs text-muted-foreground bg-foreground rounded px-1.5 py-1 mt-0.5 font-mono space-y-0.5">
              <div>
                matched:{' '}
                <span
                  className={
                    entry.output.matched ? 'text-green-400' : 'text-amber-400'
                  }
                >
                  {String(entry.output.matched)}
                </span>
              </div>
              {entry.output.configLabel && (
                <div>config: {entry.output.configLabel}</div>
              )}
              <div>materials: {entry.output.materialCount}</div>
              <div>subtotal: {currency(entry.output.subtotal)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Panel ────────────────────────────────────────────── */

export default function EngineDebugPanel() {
  const [visible, setVisible] = useState(true);

  // Subscribe to the debug store
  const entries = useSyncExternalStore(
    subscribeDebug,
    getDebugEntries,
    getDebugEntries,
  );

  // Re-check debug flag on mount + URL changes
  const [enabled, setEnabled] = useState(isEngineDebugEnabled);
  useEffect(() => {
    // Re-evaluate after navigation (SPA route changes)
    const check = () => setEnabled(isEngineDebugEnabled());
    window.addEventListener('popstate', check);
    // Also check on interval for ?debug=true added manually
    const id = setInterval(check, 2000);
    return () => {
      window.removeEventListener('popstate', check);
      clearInterval(id);
    };
  }, []);

  const handleClear = useCallback(() => clearDebugEntries(), []);

  if (!enabled) return null;

  // Collapsed badge
  if (!visible) {
    return (
      <button
        className="fixed bottom-4 right-4 z-50 bg-foreground text-muted-foreground rounded-full p-2 hover:bg-foreground transition-colors border border-border-subtle"
        onClick={() => setVisible(true)}
        title="Open Engine Debug Panel"
      >
        <Bug className="h-4 w-4" />
        {entries.length > 0 && (
          <span className="absolute -top-1 -right-1 h-4 w-4 bg-blue-500 text-primary-foreground text-xs rounded-full flex items-center justify-center">
            {entries.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 max-h-[420px] bg-foreground text-primary-foreground rounded-lg  border border-border-subtle flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle bg-foreground/50 shrink-0">
        <div className="flex items-center gap-1.5">
          <Bug className="h-3.5 w-3.5 text-blue-400" />
          <span className="text-xs font-semibold tracking-wide">
            Engine Debug
          </span>
          <span className="text-xs text-muted-foreground">
            ({entries.length})
          </span>
        </div>
        <div className="flex items-center gap-1">
          {entries.length > 0 && (
            <button
              className="p-1 hover:bg-foreground-soft rounded transition-colors"
              onClick={handleClear}
              title="Clear all"
            >
              <Trash2 className="h-3 w-3 text-muted-foreground" />
            </button>
          )}
          <button
            className="p-1 hover:bg-foreground-soft rounded transition-colors"
            onClick={() => setVisible(false)}
            title="Minimize"
          >
            <X className="h-3 w-3 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {entries.length === 0 ? (
          <div className="px-3 py-6 text-center">
            <p className="text-xs text-muted-foreground">
              No engine runs yet. Navigate to an intake form to see results
              here.
            </p>
          </div>
        ) : (
          entries.map((e, i) => <DebugRow key={`${e.timestamp}-${i}`} entry={e} />)
        )}
      </div>
    </div>
  );
}
